import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "./global.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { NodusProvider } from "@nodus/ui";

import { initSentry } from "./lib/sentry";
import { getConfig } from "./lib/config";
import { rememberSiteFromUrl, vanityRedirect } from "./lib/vanity";
import { installInteractionTracker } from "./lib/doorbell-call/ringtone";
import App from "./App";
import { NodusCommentsProvider } from "./components/comments-provider";

// ── One portal address ───────────────────────────────────────────────
// On a site's own domain (the on-site copy, which knows its site) go to member.nodus.<env> with ?site=<id>
// before anything renders; there, remember ?site= for the login page's logo (lib/vanity.ts).
const vanityTarget = vanityRedirect(
  window.location,
  window.__CONFIG__?.SITE_ID,
  new URL(getConfig().API_BASE_URL!).hostname,
);
if (vanityTarget) window.location.replace(vanityTarget);
else rememberSiteFromUrl();

// ── Stale-deploy chunk recovery ──────────────────────────────────────
// After a release, tabs still running the previous bundle try to lazy-
// load route chunks whose hashed filenames no longer exist (Cloudflare
// Pages only serves the newest deployment's assets) — menu clicks then
// silently do nothing. Vite emits ``vite:preloadError`` for exactly this;
// reload once to pick up the new bundle. Time-guarded so a genuine asset
// outage surfaces as an error instead of a reload loop.
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'nodus-chunk-reload-at';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 30_000) return;
  sessionStorage.setItem(KEY, String(Date.now()));
  event.preventDefault();
  window.location.reload();
});


// Initialize Sentry if DSN is configured
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  initSentry(sentryDsn);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

// The visitor-call ringtone may only sound after the first interaction (autoplay rules).
installInteractionTracker(window);

// Leaving for member.nodus: nothing to render here.
if (!vanityTarget) createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <NodusProvider colorScheme="auto">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <NodusCommentsProvider>
            <App />
            <Toaster position="top-center" />
          </NodusCommentsProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </NodusProvider>
  </StrictMode>
);
